"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

type RestaurantOrder = {
  id: number;
  status: string;
  totalAmount: number;
  fulfilmentMethod: string;
  createdAt: string;

  seller: {
    id: number;
    name: string;
    mobile: string;
    foodSellerName?: string | null;
    foodSellerType?: string | null;
    address?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  };
};

export default function MyRestaurantOrdersPage() {
  const [orders, setOrders] =
    useState<RestaurantOrder[]>([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [cancellingId, setCancellingId] =
    useState<number | null>(null);

  const [pickingUpId, setPickingUpId] =
    useState<number | null>(null);

  const [actionMessage, setActionMessage] =
    useState("");
  const [deliveryOtpValues, setDeliveryOtpValues] =
    useState<Record<number, string>>({});
  const [deliveryOtpLoadingId, setDeliveryOtpLoadingId] =
    useState<number | null>(null);
  const [deliveryOtpError, setDeliveryOtpError] =
    useState<Record<number, string>>({});
  const [deliveryOtpExpiresAt, setDeliveryOtpExpiresAt] =
    useState<Record<number, string>>({});
  const [deliveryOtpNow, setDeliveryOtpNow] = useState(0);

  useEffect(() => {
    setDeliveryOtpNow(Date.now());

    const interval = window.setInterval(() => {
      setDeliveryOtpNow(Date.now());
    }, 1000);

    return () => window.clearInterval(interval);
  }, []);


  useEffect(() => {
    let cancelled = false;

    async function recoverDeliveryOtps() {
      try {
        const [meResponse, ordersResponse] = await Promise.all([
          fetch("/api/me", { cache: "no-store" }),
          fetch("/api/restaurant-orders", { cache: "no-store" }),
        ]);

        if (cancelled) return;

        if (!meResponse.ok || !ordersResponse.ok) {
          window.sessionStorage.removeItem("restaurantDeliveryOtps");
          return;
        }

        const me = await meResponse.json();
        const orders = await ordersResponse.json();

        if (
          !Number.isSafeInteger(me?.id) ||
          !Array.isArray(orders)
        ) {
          window.sessionStorage.removeItem("restaurantDeliveryOtps");
          return;
        }

        const activeIds = new Set(
          orders
            .filter((order: RestaurantOrder) =>
              order.fulfilmentMethod === "SellerDelivery" &&
              order.status === "OutForDelivery"
            )
            .map((order: RestaurantOrder) => String(order.id))
        );

        const raw = window.sessionStorage.getItem("restaurantDeliveryOtps");
        if (!raw) return;

        const saved = JSON.parse(raw) as Record<string, {
          otp?: string;
          expiresAt?: string;
          customerId?: number;
        }>;

        const values: Record<number, string> = {};
        const expiries: Record<number, string> = {};

        for (const [id, item] of Object.entries(saved)) {
          const orderId = Number(id);

          if (
            !activeIds.has(id) ||
            item?.customerId !== me.id ||
            !Number.isSafeInteger(orderId) ||
            !item ||
            typeof item.otp !== "string" ||
            !/^\d{4}$/.test(item.otp) ||
            typeof item.expiresAt !== "string" ||
            !Number.isFinite(Date.parse(item.expiresAt)) ||
            Date.parse(item.expiresAt) <= Date.now()
          ) continue;

          values[orderId] = item.otp;
          expiries[orderId] = item.expiresAt;
        }

        if (cancelled) return;

        setDeliveryOtpValues(values);
        setDeliveryOtpExpiresAt(expiries);

        const retained = Object.fromEntries(
          Object.keys(values).map(id => [
            id,
            {
              otp: values[Number(id)],
              expiresAt: expiries[Number(id)],
              customerId: me.id,
            },
          ])
        );

        if (Object.keys(retained).length) {
          window.sessionStorage.setItem(
            "restaurantDeliveryOtps",
            JSON.stringify(retained)
          );
        } else {
          window.sessionStorage.removeItem("restaurantDeliveryOtps");
        }
      } catch {
        if (!cancelled) {
          try {
            window.sessionStorage.removeItem("restaurantDeliveryOtps");
          } catch {
            // Browser storage may be unavailable.
          }
        }
      }
    }

    void recoverDeliveryOtps();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (deliveryOtpNow === 0) return;

    const expiredIds = Object.keys(deliveryOtpExpiresAt).filter(id => {
      const expiresAt = Date.parse(deliveryOtpExpiresAt[Number(id)]);
      return !Number.isFinite(expiresAt) || expiresAt <= deliveryOtpNow;
    });

    if (expiredIds.length === 0) return;

    const expired = new Set(expiredIds);

    setDeliveryOtpValues(current =>
      Object.fromEntries(
        Object.entries(current).filter(([id]) => !expired.has(id))
      )
    );

    try {
      const raw = window.sessionStorage.getItem("restaurantDeliveryOtps");
      if (raw) {
        const saved = JSON.parse(raw) as Record<string, unknown>;
        for (const id of expiredIds) {
          delete saved[id];
        }
        window.sessionStorage.setItem(
          "restaurantDeliveryOtps",
          JSON.stringify(saved)
        );
      }
    } catch {
      window.sessionStorage.removeItem("restaurantDeliveryOtps");
    }
  }, [deliveryOtpNow, deliveryOtpExpiresAt]);

  useEffect(() => {
    void loadOrders();
  }, []);

  async function requestDeliveryOtp(orderId: number) {
    if (deliveryOtpLoadingId !== null) return;

    setDeliveryOtpLoadingId(orderId);
    setDeliveryOtpError(current => ({
      ...current,
      [orderId]: "",
    }));

    try {
      const response = await fetch(
        `/api/restaurant-orders/${orderId}/delivery-otp`,
        {
          method: "POST",
          cache: "no-store",
        }
      );

      const data: {
        otp?: string;
        expiresAt?: string;
        message?: string;
      } = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Unable to issue delivery OTP.");
      }

      if (!data.otp || !/^\d{4}$/.test(data.otp)) {
        throw new Error("Invalid OTP response.");
      }

      if (
        !data.expiresAt ||
        !Number.isFinite(Date.parse(data.expiresAt)) ||
        Date.parse(data.expiresAt) <= Date.now()
      ) {
        throw new Error("Invalid OTP expiry response.");
      }

      setDeliveryOtpValues(current => ({
        ...current,
        [orderId]: data.otp!,
      }));
      setDeliveryOtpExpiresAt(current => ({
        ...current,
        [orderId]: data.expiresAt!,
      }));
      try {
        const meResponse = await fetch("/api/me", {
          cache: "no-store",
        });

        if (!meResponse.ok) {
          throw new Error("Customer session could not be verified.");
        }

        const me: { id?: number } = await meResponse.json();

        if (!Number.isSafeInteger(me.id) || !me.id || me.id <= 0) {
          throw new Error("Invalid customer session.");
        }

        const raw = window.sessionStorage.getItem("restaurantDeliveryOtps");
        const saved = raw ? JSON.parse(raw) : {};

        window.sessionStorage.setItem(
          "restaurantDeliveryOtps",
          JSON.stringify({
            ...saved,
            [orderId]: {
              otp: data.otp,
              expiresAt: data.expiresAt,
              customerId: me.id,
            },
          })
        );
      } catch {
        // Browser storage may be unavailable or session may have changed.
      }
    } catch (error) {
      setDeliveryOtpError(current => ({
        ...current,
        [orderId]: error instanceof Error
          ? error.message
          : "Unable to issue delivery OTP.",
      }));
    } finally {
      setDeliveryOtpLoadingId(null);
    }
  }

  async function loadOrders() {
    try {
      setLoading(true);
      setError("");

      const response =
        await fetch(
          "/api/restaurant-orders",
          {
            cache: "no-store",
          }
        );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
            "Unable to load your restaurant orders"
        );
      }

      const currentOrders: RestaurantOrder[] =
        Array.isArray(data) ? data : [];

      setOrders(currentOrders);

      // Retain OTPs only for this customer's active deliveries.
      const activeOrderIds = new Set(
        currentOrders
          .filter(order =>
            order.fulfilmentMethod === "SellerDelivery" &&
            order.status === "OutForDelivery"
          )
          .map(order => String(order.id))
      );

      setDeliveryOtpValues(current =>
        Object.fromEntries(
          Object.entries(current).filter(([id]) =>
            activeOrderIds.has(id)
          )
        )
      );

      setDeliveryOtpExpiresAt(current =>
        Object.fromEntries(
          Object.entries(current).filter(([id]) =>
            activeOrderIds.has(id)
          )
        )
      );

      try {
        const raw = window.sessionStorage.getItem("restaurantDeliveryOtps");
        if (raw) {
          const saved = JSON.parse(raw) as Record<string, unknown>;
          const retained = Object.fromEntries(
            Object.entries(saved).filter(([id]) =>
              activeOrderIds.has(id)
            )
          );

          if (Object.keys(retained).length > 0) {
            window.sessionStorage.setItem(
              "restaurantDeliveryOtps",
              JSON.stringify(retained)
            );
          } else {
            window.sessionStorage.removeItem("restaurantDeliveryOtps");
          }
        }
      } catch {
        try {
          window.sessionStorage.removeItem("restaurantDeliveryOtps");
        } catch {
          // Browser storage may be unavailable.
        }
      }
    } catch (error) {
      console.error(
        "Unable to load buyer restaurant orders:",
        error
      );

      setError(
        error instanceof Error
          ? error.message
          : "Unable to load your restaurant orders"
      );
    } finally {
      setLoading(false);
    }
  }

  async function cancelOrder(
    orderId: number
  ) {
    const confirmed =
      window.confirm(
        "Are you sure you want to cancel this restaurant order?"
      );

    if (!confirmed) {
      return;
    }

    try {
      setCancellingId(orderId);
      setActionMessage("");
      setError("");

      const response =
        await fetch(
          `/api/restaurant-orders/${orderId}`,
          {
            method: "PATCH",
            headers: {
              "Content-Type":
                "application/json",
            },
            body: JSON.stringify({
              action: "cancel",
            }),
          }
        );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
            "Unable to cancel restaurant order"
        );
      }

      setOrders((current) =>
        current.map((order) =>
          order.id === orderId
            ? data
            : order
        )
      );

      setActionMessage(
        `Restaurant order #${orderId} cancelled successfully.`
      );
    } catch (error) {
      console.error(
        "Unable to cancel restaurant order:",
        error
      );

      setActionMessage(
        error instanceof Error
          ? error.message
          : "Unable to cancel restaurant order"
      );

      await loadOrders();
    } finally {
      setCancellingId(null);
    }
  }

  async function confirmPickup(
    orderId: number
  ) {
    const confirmed =
      window.confirm(
        "Have you collected your complete restaurant order from the restaurant?"
      );

    if (!confirmed) {
      return;
    }

    try {
      setPickingUpId(orderId);
      setActionMessage("");
      setError("");

      const response =
        await fetch(
          `/api/restaurant-orders/${orderId}`,
          {
            method: "PATCH",
            headers: {
              "Content-Type":
                "application/json",
            },
            body: JSON.stringify({
              action: "picked_up",
            }),
          }
        );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
            "Unable to confirm pickup"
        );
      }

      setOrders((current) =>
        current.map((order) =>
          order.id === orderId
            ? data
            : order
        )
      );

      setActionMessage(
        `✅ Restaurant order #${orderId} pickup confirmed successfully.`
      );
    } catch (error) {
      console.error(
        "Unable to confirm restaurant order pickup:",
        error
      );

      setActionMessage(
        error instanceof Error
          ? error.message
          : "Unable to confirm pickup"
      );

      await loadOrders();
    } finally {
      setPickingUpId(null);
    }
  }

  function formatDate(
    value: string
  ) {
    const date =
      new Date(value);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return value;
    }

    return date.toLocaleString();
  }

  function statusClass(
    status: string
  ) {
    if (
      status === "Pending"
    ) {
      return "bg-yellow-100 text-yellow-800";
    }

    if (
      status === "Accepted"
    ) {
      return "bg-green-100 text-green-800";
    }

    if (
      status === "Ready"
    ) {
      return "bg-blue-100 text-blue-800";
    }

    if (
      status === "PickedUp"
    ) {
      return "bg-purple-100 text-purple-800";
    }

    if (
      status === "Rejected"
    ) {
      return "bg-red-100 text-red-700";
    }

    if (
      status === "Cancelled"
    ) {
      return "bg-gray-200 text-gray-700";
    }

    return "bg-gray-100 text-gray-700";
  }

  function statusMessage(
    status: string
  ) {
    if (
      status === "Pending"
    ) {
      return "Waiting for the restaurant to accept your order.";
    }

    if (
      status === "Accepted"
    ) {
      return "Your order has been accepted by the restaurant.";
    }

    if (
      status === "Ready"
    ) {
      return "✅ Your order is ready for pickup. You can now collect it from the restaurant.";
    }

    if (
      status === "PickedUp"
    ) {
      return "✅ You have picked up your restaurant order successfully.";
    }

    if (
      status === "Rejected"
    ) {
      return "The restaurant rejected this order.";
    }

    if (
      status === "Cancelled"
    ) {
      return "You cancelled this order.";
    }

    return `Order status: ${status}`;
  }

  function openOrderDetails(
    orderId: number
  ) {
    window.location.href =
      `/restaurant-order-details/${orderId}`;
  }

  return (
    <main className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <Link
            href="/buy-food"
            className="inline-flex items-center text-orange-700 font-bold hover:underline"
          >
            ← Back to Buy Food
          </Link>
        </div>

        <section className="bg-white rounded-2xl shadow p-5 md:p-7 mb-6">
          <h1 className="text-2xl md:text-3xl font-black text-orange-600">
            🍽️ My Restaurant Orders
          </h1>

          <p className="mt-2 text-gray-600">
            Track the orders you placed from restaurants.
          </p>
        </section>

        {actionMessage && (
          <div className="mb-5 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm font-bold text-orange-800">
            {actionMessage}
          </div>
        )}

        {loading && (
          <div className="bg-white rounded-2xl shadow p-10 text-center">
            <p className="text-lg text-gray-500">
              Loading your restaurant orders...
            </p>
          </div>
        )}

        {!loading &&
          error && (
            <div className="bg-white rounded-2xl shadow p-8 text-center">
              <p className="font-bold text-red-600">
                {error}
              </p>

              <button
                type="button"
                onClick={
                  loadOrders
                }
                className="mt-4 px-5 py-3 rounded-xl bg-orange-600 text-white font-bold"
              >
                Try Again
              </button>
            </div>
          )}

        {!loading &&
          !error &&
          orders.length === 0 && (
            <div className="bg-white rounded-2xl shadow p-10 text-center">
              <div className="text-6xl mb-4">
                🧾
              </div>

              <h2 className="text-xl font-black text-gray-900">
                No restaurant orders yet
              </h2>

              <p className="mt-2 text-gray-600">
                Restaurant orders you place will appear here.
              </p>
            </div>
          )}

        {!loading &&
          !error &&
          orders.length > 0 && (
            <div className="space-y-5">
              {orders.map(
                (order) => {
                  const restaurantName =
                    order.seller
                      .foodSellerName ||
                    order.seller
                      .name ||
                    "Restaurant";

                  return (
                    <article
                      key={order.id}
                      role="link"
                      tabIndex={0}
                      onClick={() =>
                        openOrderDetails(
                          order.id
                        )
                      }
                      onKeyDown={(event) => {
                        if (
                          event.key ===
                            "Enter" ||
                          event.key ===
                            " "
                        ) {
                          event.preventDefault();
                          openOrderDetails(
                            order.id
                          );
                        }
                      }}
                      className="bg-white rounded-2xl shadow border p-5 md:p-6 cursor-pointer hover:shadow-lg hover:border-orange-300 transition focus:outline-none focus:ring-2 focus:ring-orange-500"
                      aria-label={`Open details for restaurant order #${order.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div>
                          <p className="text-sm font-bold text-gray-500">
                            Order ID
                          </p>

                          <h2 className="mt-1 text-xl font-black text-gray-900">
                            #{order.id}
                          </h2>

                          <p className="mt-3 text-lg font-black text-orange-600">
                            🍽️ {restaurantName}
                          </p>

                          <p className="mt-1 text-sm text-gray-500">
                            {formatDate(
                              order.createdAt
                            )}
                          </p>

                          <p className="mt-3 text-xs font-bold text-orange-600">
                            Tap to view order details →
                          </p>
                        </div>

                        <div className="md:text-right">
                          <p className="text-sm font-bold text-gray-500">
                            Order Total
                          </p>

                          <p className="mt-1 text-2xl font-black text-green-700">
                            ₹
                            {order.totalAmount.toFixed(
                              2
                            )}
                          </p>

                          <span
                            className={`mt-3 inline-flex px-3 py-1.5 rounded-full text-sm font-black ${statusClass(
                              order.status
                            )}`}
                          >
                            {order.status ===
                            "PickedUp"
                              ? "Picked Up"
                              : order.status}
                          </span>

                          {order.fulfilmentMethod ===
                            "SelfPickup" && (
                            <p className="mt-2 text-sm font-bold text-gray-600">
                              Self Pickup
                            </p>
                          )}

                          {order.status ===
                            "Pending" && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();

                                void cancelOrder(
                                  order.id
                                );
                              }}
                              disabled={
                                cancellingId ===
                                order.id
                              }
                              className="mt-3 inline-flex min-h-11 items-center justify-center rounded-xl bg-red-600 px-4 py-2.5 text-sm font-extrabold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {cancellingId ===
                              order.id
                                ? "Cancelling..."
                                : "❌ Cancel Order"}
                            </button>
                          )}
                        </div>
                      </div>

                      <div
                        className={`mt-4 rounded-xl p-4 font-bold ${
                          order.status ===
                          "Accepted"
                            ? "bg-green-50 text-green-800 border border-green-100"
                            : order.status ===
                                "Ready"
                              ? "bg-blue-50 text-blue-800 border border-blue-200"
                              : order.status ===
                                  "PickedUp"
                                ? "bg-purple-50 text-purple-800 border border-purple-200"
                                : order.status ===
                                    "Rejected"
                                  ? "bg-red-50 text-red-700 border border-red-100"
                                  : order.status ===
                                      "Cancelled"
                                    ? "bg-gray-100 text-gray-700 border border-gray-200"
                                    : "bg-yellow-50 text-yellow-800 border border-yellow-100"
                        }`}
                      >
                        {statusMessage(
                          order.status
                        )}
                      </div>

                      {order.status ===
                        "Ready" && (
                        <div className="mt-4 rounded-xl bg-blue-600 text-white p-4 text-center">
                          <p className="text-lg font-black">
                            🛍️ Ready for Pickup
                          </p>

                          <p className="mt-1 text-sm">
                            Please collect your order from the restaurant.
                          </p>

                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();

                              void confirmPickup(
                                order.id
                              );
                            }}
                            disabled={
                              pickingUpId ===
                              order.id
                            }
                            className="mt-4 inline-flex min-h-11 items-center justify-center rounded-xl bg-white px-5 py-3 text-sm font-black text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {pickingUpId ===
                            order.id
                              ? "Confirming Pickup..."
                              : "🛍️ Confirm Pickup"}
                          </button>
                        </div>
                      )}

                      {order.fulfilmentMethod === "SellerDelivery" &&
                        order.status === "OutForDelivery" && (
                          <div
                            className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-center"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <p className="font-bold text-indigo-900">
                              🛵 Your order is out for delivery
                            </p>
                            <p className="mt-2 text-sm text-indigo-800">
                              Share your delivery OTP only when the order arrives.
                            </p>

                            {deliveryOtpValues[order.id] &&
                            deliveryOtpExpiresAt[order.id] &&
                            deliveryOtpNow > 0 &&
                            Date.parse(deliveryOtpExpiresAt[order.id]) > deliveryOtpNow ? (
                              <div className="mt-3 text-center">
                                <p className="text-3xl font-black tracking-widest text-indigo-950">
                                  {deliveryOtpValues[order.id]}
                                </p>
                                {deliveryOtpExpiresAt[order.id] && (
                                  <p className="mt-2 text-sm font-semibold text-indigo-800">
                                    ⏰ OTP valid until:{" "}
                                    {new Date(deliveryOtpExpiresAt[order.id]).toLocaleTimeString("en-IN", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => void requestDeliveryOtp(order.id)}
                                disabled={deliveryOtpLoadingId !== null}
                                className="mt-3 rounded-xl bg-indigo-600 px-5 py-3 font-bold text-white disabled:opacity-50"
                              >
                                {deliveryOtpLoadingId === order.id
                                  ? "Generating..."
                                  : deliveryOtpExpiresAt[order.id] &&
                                    deliveryOtpNow > 0 &&
                                    Date.parse(deliveryOtpExpiresAt[order.id]) <= deliveryOtpNow
                                    ? "🔄 Generate New OTP"
                                    : "🔐 Show Delivery OTP"}
                              </button>
                            )}

                            {deliveryOtpError[order.id] && (
                              <p role="alert" className="mt-2 text-sm text-red-600">
                                {deliveryOtpError[order.id]}
                              </p>
                            )}
                          </div>
                        )}

                      {order.status ===
                        "PickedUp" && (
                        <div className="mt-4 rounded-xl bg-purple-600 text-white p-4 text-center">
                          <p className="text-lg font-black">
                            ✅ Order Picked Up
                          </p>

                          <p className="mt-1 text-sm">
                            Your restaurant order has been collected successfully.
                          </p>
                        </div>
                      )}

                      {(order.status ===
                        "Accepted" ||
                        order.status ===
                          "Ready") && (
                        <div className="mt-4 rounded-xl bg-blue-50 border border-blue-100 p-4">
                          <p className="font-bold text-blue-900">
                            Restaurant contact
                          </p>

                          <p className="mt-1 text-blue-800">
                            📱 {order.seller.mobile}
                          </p>

                          {order.seller
                            .address && (
                            <p className="mt-1 text-blue-800">
                              📍{" "}
                              {
                                order.seller.address
                              }
                            </p>
                          )}
                        </div>
                      )}
                    </article>
                  );
                }
              )}
            </div>
          )}
      </div>
    </main>
  );
}